/**
 * Keeps the "Add to Cart" button and stock status in sync with the selected variant when a variant
 * picker app (e.g. Globo Color Swatch) is used instead of the theme's variant picker block.
 *
 * These apps update the product form's variant ID input (and the ?variant= URL parameter), but not
 * the rest of the product info, so a sold out variant can still show "Add to Cart" and the stock
 * status of the previously selected variant.
 *
 * The theme's 'on:variant:change' event isn't dispatched, as its listeners (e.g. media gallery)
 * expect variant data the app doesn't provide.
 *
 * Required translation strings:
 * - noStock
 * - noVariant
 */

if (!customElements.get('app-variant-sync')) {
  class AppVariantSync extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      this.form = this.section?.querySelector('.js-product-form-main');
      this.idInput = this.form?.querySelector('input[name="id"]');
      this.addBtn = this.form?.querySelector('[name="add"]');
      if (!this.idInput || !this.addBtn) return;

      this.variants = JSON.parse(this.querySelector('[type="application/json"]').textContent);

      // Listen in the capture phase, as apps don't always dispatch bubbling events
      this.boundScheduleUpdate = this.scheduleUpdate.bind(this);
      this.section.addEventListener('change', this.boundScheduleUpdate, true);
      this.section.addEventListener('input', this.boundScheduleUpdate, true);

      this.update();
    }

    disconnectedCallback() {
      if (!this.boundScheduleUpdate) return;
      this.section.removeEventListener('change', this.boundScheduleUpdate, true);
      this.section.removeEventListener('input', this.boundScheduleUpdate, true);
    }

    /**
     * Defers the update until the app has finished writing the new variant ID.
     */
    scheduleUpdate() {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = setTimeout(this.update.bind(this), 0);
    }

    /**
     * Updates the product info for the variant in the form.
     */
    update() {
      const variantId = this.idInput.value;

      // No variant selected yet - keep the server rendered state
      if (!variantId) return;

      const variant = this.variants[variantId];
      this.updateAddToCartButton(variant);
      this.updateInventoryStatus(variant);
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     * @param {?object} variant - Selected variant data, undefined if not found.
     */
    updateAddToCartButton(variant) {
      if (!variant) {
        this.setAddBtnState(false, theme.strings.noVariant);
      } else if (variant.available) {
        this.setAddBtnState(true, this.addBtn.dataset.addToCartText);
      } else {
        this.setAddBtnState(false, theme.strings.noStock);
      }
    }

    /**
     * Sets the button state without removing optional CTA icons.
     * @param {boolean} enabled - Whether the button can be clicked.
     * @param {string} text - Button label.
     */
    setAddBtnState(enabled, text) {
      this.addBtn.disabled = !enabled;
      const label = this.addBtn.querySelector('.js-add-to-cart-text');
      if (label) {
        label.textContent = text;
      } else {
        this.addBtn.textContent = text;
      }
    }

    /**
     * Updates the stock status block (snippets/product-inventory.liquid).
     * @param {?object} variant - Selected variant data, undefined if not found.
     */
    updateInventoryStatus(variant) {
      this.section.querySelectorAll('product-inventory').forEach((inventory) => {
        const quantity = variant ? variant.inventory_quantity : 0;
        const available = variant ? variant.available : false;
        const policy = variant ? variant.inventory_policy : '';

        // Data read by the block when it initialises, and on 'on:variant:change'
        inventory.dataset.inventoryQuantity = quantity;
        inventory.dataset.variantAvailable = available;
        inventory.dataset.inventoryPolicy = policy;
        const dataEl = inventory.querySelector('[type="application/json"]');
        if (dataEl) {
          dataEl.textContent = variant ? JSON.stringify({
            inventory_quantity: quantity,
            available,
            inventory_policy: policy
          }) : '{}';
        }

        // The block initialises lazily - if it hasn't yet, it will use the data above
        if (!inventory.inventoryNotice) return;

        if (variant) {
          inventory.updateInventory(quantity, available, policy);
        } else {
          inventory.updateInventory(0, false);
          inventory.hidden = true;
        }
      });
    }
  }

  customElements.define('app-variant-sync', AppVariantSync);
}
